import React from "react";
import { Building2, Landmark, TrendingUp, Rocket, Globe, ShieldCheck, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const clientCategories = [{
  title: "Enterprise Leaders",
  description: "Multi-national organizations executing high-stakes digital transitions and integrating sophisticated AI ecosystems across global markets.",
  icon: Building2
}, {
  title: "Public Institutions",
  description: "Civic and governmental bodies focused on implementing transparent AI frameworks and modernizing infrastructure for public benefit.",
  icon: Landmark
}, {
  title: "Strategic Investors",
  description: "Capital firms requiring technical precision in due diligence and strategic foresight for their technology-driven portfolio assets.",
  icon: TrendingUp
}, {
  title: "Tech Scale-ups",
  description: "High-velocity companies building the future of software that require scalable governance to sustain rapid, ethical expansion.",
  icon: Rocket
}, {
  title: "Impact Organizations",
  description: "Mission-oriented entities leveraging data science and automation to maximize social outcomes while ensuring absolute data privacy.",
  icon: Globe
}, {
  title: "Regulated Sectors",
  description: "Finance, health, and infrastructure providers navigating stringent compliance landscapes for automated and critical systems.",
  icon: ShieldCheck
}];
export default function WhoWeAdvise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="5369" id="who-this-service-is-for" className="relative w-full py-24 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-sans tracking-tight text-foreground mb-4">
              Who We Serve
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
              We partner with visionary leaders to architect responsible, scalable, and high-impact strategies for the next era of innovation.
            </p>
          </div>

          <div className="space-y-4">
            {clientCategories.map((category, i) => <div key={i} data-index={i} className={`transition-all duration-500 ease-out delay-[${i * 100}ms] ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}>
                <Card className="group hover:shadow-xl hover:-translate-y-1 transition-all duration-400 ease-[cubic-bezier(0.4,0,0.2,1)] border-border/60 bg-white/70 backdrop-blur-md rounded-[8px] hover:border-primary">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center p-6 gap-6">
                    <div className="shrink-0">
                      <div className="flex items-center justify-center w-14 h-14 rounded-lg bg-secondary/5 text-secondary group-hover:bg-primary group-hover:text-white transition-colors duration-400">
                        <category.icon className="w-7 h-7" />
                      </div>
                    </div>
                    <div className="flex-1 space-y-1">
                      <h3 className="text-xl font-bold font-sans text-foreground group-hover:text-primary transition-colors duration-400">
                        {category.title}
                      </h3>
                      <p className="text-muted-foreground font-sans leading-relaxed">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>)}
          </div>

          <div className="mt-12 text-center">
            <Button asChild size="lg" className="rounded-full px-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-all duration-300">
              <Link to="#">
                Partner With Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}